// Plan 08-05 / Plan 3 (two-step turn) — turn submission with Idempotency-Key.
//
// Wraps `apiFetch` from Plan 02 (which already implements 5xx single-retry,
// 401 → clearAuth, V1 versioning headers). This module's job is solely:
//   1. Generate a UUIDv4 Idempotency-Key (or honor a caller-supplied key).
//   2. POST to the relevant turn-step endpoint with the key on the header.
//   3. Bubble the typed response through.
//
// Plan 3 splits the legacy combined `POST /api/match/{id}/turn` into two
// sequential step endpoints:
//   - submitWord  → POST /api/match/{id}/turn/word  (Phase-A)
//   - submitChess → POST /api/match/{id}/turn/chess (Phase-B)
//
// Idempotency-Key semantics (Phase 3 FIFO-50 cache):
//   - Same key on transient 5xx retry → backend returns cached response.
//   - New key on user re-submit (4xx UX path) → fresh attempt.
// Plan 02's apiFetch passes the `idempotencyKey` field through verbatim on
// retry, so we satisfy the same-key-on-retry contract by simply NOT
// re-generating the key inside the retry loop.

import type { PlayerView } from '../game/PlayerViewSchema'
import type {
  PhaseASelection,
  PhaseBComposite,
  StagedChessEmpower,
  StagedChessExtend,
  StagedChessMove,
  StagedChessPowerup,
} from '../stores/turnBuilder'
import { apiFetch } from './client'
import { createIdempotencyKey } from './idempotency'

export interface SubmitTurnOptions {
  /** If provided, skips internal generation. Caller is responsible for UUIDv4 format. */
  idempotencyKey?: string
}

/** Phase-A (word) step request body — mirrors backend /turn/word contract. */
export interface WordSubmissionBody {
  readonly turnIdx: number
  readonly player_slot: 0 | 1
  readonly phase_a: PhaseASelection
}

/**
 * Backend `CompositeChessStep` wire body (ARCA-619). Each sub-action is
 * optional; an empty `{}` is the do-nothing step that replaces the legacy Pass
 * (D4). The server applies present sub-actions atomically in canonical order
 * powerup → empower/extend → move (D3), regardless of the order they were
 * staged. The sub-object shapes are identical to the staged-slot types (no
 * `kind` discriminator) — see openapi.yaml `CompositeChessStep`.
 */
export interface CompositeChessStepBody {
  move?: StagedChessMove
  empower?: StagedChessEmpower
  extend?: StagedChessExtend
  powerup?: StagedChessPowerup
}

/** Phase-B (chess) step request body — mirrors backend /turn/chess contract. */
export interface ChessSubmissionBody {
  readonly turnIdx: number
  readonly player_slot: 0 | 1
  readonly phase_b: CompositeChessStepBody
}

/**
 * Project the live `$turnBuilder.phaseB` composite into the `CompositeChessStep`
 * wire body, omitting unstaged (null) slots. An all-null composite yields `{}`
 * — the do-nothing step. (`empower` XOR `extend` is already enforced by the
 * store's stage setters, so at most one role slot is ever non-null.)
 */
export function toChessStepBody(
  phaseB: PhaseBComposite,
): CompositeChessStepBody {
  const body: CompositeChessStepBody = {}
  if (phaseB.powerup !== null) body.powerup = phaseB.powerup
  if (phaseB.empower !== null) body.empower = phaseB.empower
  if (phaseB.extend !== null) body.extend = phaseB.extend
  if (phaseB.move !== null) body.move = phaseB.move
  return body
}

/** Response from POST /api/match/{id}/turn/word. */
export interface WordStepResponse {
  readonly accepted: boolean
  readonly your_state: PlayerView
  readonly tokens_earned: number
  readonly word_score: number
  readonly phase_a_word_rejected: boolean
}

/** Response from POST /api/match/{id}/turn/chess. */
export interface ChessStepResponse {
  readonly accepted: boolean
  readonly your_state: PlayerView
  readonly affinity_reveal?: unknown
  // ARCA-152 — terminal-only score + captures reveal (both slots). Present on
  // the chess commit that ends the match; omitted otherwise. The live overlay
  // consumes the reveal via the WS `_score_reveal` sidecar ($scoreReveal); this
  // field keeps the HTTP contract symmetric with `affinity_reveal`.
  readonly score_reveal?: unknown
}

/**
 * POST /api/match/{matchId}/turn/word with an `Idempotency-Key` header.
 *
 * Submits the Phase-A (word) half of a two-step turn. Same idempotency /
 * retry semantics as the legacy combined endpoint: 5xx → one retry with the
 * SAME key; 4xx (non-401) → ApiError; 401 → clearAuth + AuthInvalidError.
 */
export async function submitWord(
  matchId: string,
  body: WordSubmissionBody,
  opts: SubmitTurnOptions = {},
): Promise<WordStepResponse> {
  const idempotencyKey = opts.idempotencyKey ?? createIdempotencyKey()
  return apiFetch<WordStepResponse>(`/api/match/${matchId}/turn/word`, 'POST', {
    body,
    idempotencyKey,
  })
}

/**
 * POST /api/match/{matchId}/turn/chess with an `Idempotency-Key` header.
 *
 * Submits the Phase-B (chess) half of a two-step turn. Same idempotency /
 * retry semantics as submitWord.
 */
export async function submitChess(
  matchId: string,
  body: ChessSubmissionBody,
  opts: SubmitTurnOptions = {},
): Promise<ChessStepResponse> {
  const idempotencyKey = opts.idempotencyKey ?? createIdempotencyKey()
  return apiFetch<ChessStepResponse>(
    `/api/match/${matchId}/turn/chess`,
    'POST',
    { body, idempotencyKey },
  )
}

// Plan 3 Task 4 — the deprecated combined-step `submitTurn` (POST
// /api/match/{id}/turn) was removed once MatchScreen migrated to the
// two-step submitWord/submitChess flow. The backend `/turn` route teardown
// follows in Plan 3 Tasks 5–6.
